<script setup lang="ts">
import { guest, userInjectionKey, type ID } from '#shared'
import { computed, inject, ref, toRef, unref } from 'vue'
import {
  MessageAttachments,
  MessageLayout,
  MessageReactions,
  MessageResend,
  MessageRespondFor,
  MessageStatus,
  MessageText,
  type Message,
} from '#entities/message'
import { useIsMessageHighlighted } from '../lib/use-is-message-highlighted'
import MessageListItemActions from './message-list-item-actions.vue'

const {
  message,
  chatId,
  variant = 'regular',
  isTarget = false,
  navnonce,
} = defineProps<{ message: Message; chatId: ID; variant?: 'regular' | 'compact'; isTarget?: boolean; navnonce?: any }>()

const currentUser = inject(userInjectionKey)
const hasActions = computed(() => {
  const messageVal = unref(message)
  const currentUserVal = unref(currentUser)

  if (messageVal.meta?.isSending) return false
  if (messageVal.meta?.isErrored) return false
  if (!currentUserVal || currentUserVal === guest) return false

  return true
})

const isHighlighted = useIsMessageHighlighted(
  toRef(() => isTarget),
  toRef(() => navnonce)
)

const isHovered = ref(false)
const onHover = () => (isHovered.value = true)
const onLeave = () => (isHovered.value = false)
</script>

<template>
  <MessageLayout
    :message="message"
    :variant="variant"
    :isHighlighted="isHighlighted"
    @mouseenter="onHover"
    @mouseleave="onLeave"
  >
    <template v-if="message.respondFor" #preContent> </template>

    <template #header>
      <MessageStatus :message="message" :variant="variant" />
    </template>

    <template #main>
      <MessageRespondFor v-if="message.respondFor" :respondFor="message.respondFor" :variant="variant" />

      <MessageAttachments :message="message" :variant="variant" />

      <MessageText :message="message" :variant="variant" />

      <MessageReactions v-if="!message.meta?.isSending && !message.meta?.isErrored" :message="message" />

      <MessageResend v-if="message.meta?.isErrored" :message="message" :chatId="chatId" />
    </template>

    <template #overlay>
      <MessageListItemActions v-if="hasActions" :message="message" :isMessageHovered="isHovered" />
    </template>
  </MessageLayout>
</template>

<style lang="scss" module></style>
